import { CATEGORY, ENTERPRISE_PARTNER_ID } from '@brighte/enterprise-common/constants';
import { MaskitoOptions } from '@maskito/core';
import { Heading } from '@spark-web/heading';
import { Inline } from '@spark-web/inline';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { TextList } from '@spark-web/text-list';
import { TFunction } from 'i18next';
import { ReactElement } from 'react';
import { Trans } from 'react-i18next';

import commbankCoinIcon from './public/icons/commbank/coin.svg';
import racqMembershipIcon from './public/icons/racq/racq-membership.svg';
import { MarketplaceChannel, MarketplacePartner, MarketplaceSort } from './types/marketplace';
import { getBrighteSitePartnerUrl } from './utils/formatter';
import { validateRacqMemberId } from './utils/premarketplace/validation/racq';

/** Global configuration */
export const DEFAULT_LOGO_WIDTH = 130;
export const DEFAULT_LOGO_HEIGHT = 40;
export const DEFAULT_LIST_COUNT = 4;
export const DEFAULT_VENDOR_SELECTION_LIMIT = 5;
export const DEFAULT_CATEGORY_ICON_SIZE = 24;

export const RECOMMENDED_VENDORS_LENGTH = 3;
/**
 * Size of the top-ranked pool the recommended vendors are randomly drawn from.
 * We fetch this many vendors sorted by RANKING and randomly pick
 * RECOMMENDED_VENDORS_LENGTH of them, to spread leads across high-ranking vendors
 * rather than always surfacing the literal top 3 (INT-1041).
 */
export const RECOMMENDED_VENDORS_POOL_SIZE = 10;

export const BRIGHTE_PHONE_NUMBER = '1300 274 448';

export const ASSET_URL = import.meta.env.VITE_ASSET_URL;

export const RULE_EMAIL_PATTERN = {
  pattern: {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9-]+\.[A-Z0-9-]{2,63}(?:\.[A-Z0-9-]+)*$/i,
    message: 'Email is invalid',
  },
};

export const RULE_MOBILE_NO_PATTERN = {
  pattern: {
    value: /^04\d{2} ?\d{3} ?\d{3}$/,
    message: 'Mobile number is invalid',
  },
};

export const RULE_POSTCODE_PATTERN = {
  pattern: {
    value: /\b(0[8-9]\d{2}|[1-7]\d{3})\b/,
    message: 'Postcode is invalid',
  },
};

export const SORT_FIELD_BY_TYPE: Record<
  Exclude<MarketplaceSort, 'DEFAULT'>,
  { type?: string; direction?: 'ASC' | 'DESC'; seed?: string }
> = {
  DISTANCE: {
    type: 'distance',
    direction: 'ASC',
  },
  RANKING: {},
  PRICE_LOW_TO_HIGH: {
    type: 'categoryPrice',
    direction: 'ASC',
  },
  PRICE_HIGH_TO_LOW: {
    type: 'categoryPrice',
    direction: 'DESC',
  },
  RANDOM: {
    seed: 'opportunity-id',
  },
};

/** Channel configuration */
type ChannelConfiguration = {
  supportDetails?: () => ReactElement;
  requiredCategories?: CATEGORY[];
  validateByRequiredCategories?: boolean;
  // whether the concession card type field is required -- this field is not recorded and persisted in db
  requiresConcessionType?: boolean;
};

export const CHANNEL_REQUIREMENTS: Partial<Record<MarketplaceChannel, ChannelConfiguration>> = {
  'electrify-2515': {
    supportDetails: () => (
      <Stack gap="large">
        <Inline gap="xsmall">
          <Text size="xsmall">If you have any questions</Text>
          <Text size="xsmall">
            <TextLink href="https://www.electrify2515.org/contact" target="_blank">
              contact the Electrify 2515 Pilot Team.
            </TextLink>
          </Text>
        </Inline>
      </Stack>
    ),
    requiredCategories: [CATEGORY.SmartEnergyDevice],
    validateByRequiredCategories: true,
  },
  'act-hesp': {
    requiresConcessionType: true,
  },
};

export const CHANNEL_VENDOR_INFO: Partial<Record<MarketplaceChannel, (t: TFunction) => ReactElement>> = {
  'electrify-2515': (t) => (
    <Text size="xsmall" data-testid="electrify-2515-info">
      <Trans
        t={t}
        i18nKey="Electrify 2515 Community Pilot subsidy available—<findOutLink>find out more</findOutLink>"
        components={{
          findOutLink: <TextLink tone="info" target="_blank" href="https://www.electrify2515.org/pilot" />,
        }}
      />
    </Text>
  ),
};

type MarketplaceChannelSubsidyInfo = {
  [category: string]: {
    maxSubsidy?: number;
    customText?: (onSeeDetailsClick?: () => void) => ReactElement;
    details?: () => ReactElement;
  };
};

export const CHANNEL_SUBSIDY_INFO: Partial<Record<MarketplaceChannel, MarketplaceChannelSubsidyInfo>> = {
  // derived from finance/services/core/src/models/quotes/utils/rebate-calculator/e2515.values.ts
  'electrify-2515': {
    [CATEGORY.SmartEnergyDevice]: {
      maxSubsidy: 2500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.SmartEnergyDevice}` }}>
          <Text>
            A fully subsidised smart energy device, valued at around $1,500, installed in every home to track and
            optimise energy use.
          </Text>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.HeatPumpWaterHeater]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.HeatPumpWaterHeater}` }}>
          <Stack gap="xlarge">
            <Text>
              Subsidy of up to $1,000 to replace a gas hot water system (covering up to 50% of the installation cost).
            </Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.ElectricResistanceHotWaterSystem]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.ElectricResistanceHotWaterSystem}` }}>
          <Stack gap="xlarge">
            <Text>
              Subsidy of up to $1,000 to replace a gas hot water system (covering up to 50% of the installation cost).
            </Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.HeatPumpSpaceHeater]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.HeatPumpSpaceHeater}` }}>
          <Stack gap="xlarge">
            <Text>
              Subsidy of up to $1,000 to replace a gas hot water system (covering up to 50% of the installation cost).
            </Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.InductionCooktop]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.InductionCooktop}` }}>
          <Stack gap="xlarge">
            <Text>Subsidy of up to $1,000 to replace gas cooktop (covering up to 50% of the installation cost).</Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank" rel="noopener noreferrer">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.SwitchboardUpgrade]: {
      maxSubsidy: 3000,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.SwitchboardUpgrade}` }}>
          <Text>
            Subsidies are available for upgrades to switchboards where these are required to meet today&apos;s safety
            standards.
          </Text>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.ElectricHeatingCoolingSystemsReverseCycle]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.ElectricHeatingCoolingSystemsReverseCycle}` }}>
          <Stack gap="xlarge">
            <Text>Subsidy of up to $1,000 to replace a gas heater (covering up to 50% of the installation cost).</Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
  },
};

type CustomerIdFieldConfig = {
  label?: string;
  description?: string;
  placeholder?: string;
  icon?: () => ReactElement;
  validation?: (value: string) => Promise<boolean | null>;
  maskitoOptions?: MaskitoOptions;
};

export const PARTNER_CUSTOMER_ID_FIELD_CONFIG: Partial<Record<MarketplacePartner, CustomerIdFieldConfig>> = {
  racq: {
    label: 'RACQ membership number',
    description: 'This is your 16-digit membership number',
    placeholder: '0000 0000 0000 0000',
    icon: () => <img src={racqMembershipIcon} alt="RACQ membership icon" />,
    validation: (value?: string) => validateRacqMemberId(value),
    maskitoOptions: {
      mask: [
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ],
    },
  },
};

export const PARTNER_WEBSITE = (key?: string | null) => {
  switch (key) {
    case ENTERPRISE_PARTNER_ID.commbank:
      return 'https://www.commbank.com.au';
    case ENTERPRISE_PARTNER_ID.rewiringAustralia:
      return 'https://www.electrify2515.org/pilot';
    default:
      return 'https://brighte.com.au';
  }
};

export const PARTNER_DISCLAIMER: Partial<
  Record<MarketplacePartner, (t: TFunction) => { title: string; content: ReactElement }>
> = {
  // TODO: get from higraph based from partner (ET-587)
  // things to consider: how do we tackle links? and how do we know which word to embed, is it gonna be returned as pure html (dangerous)?
  [ENTERPRISE_PARTNER_ID.commbank]: (t) => ({
    title: 'Things you should know',
    content: (
      <Stack gap="xxlarge">
        <Text size="xsmall">
          {
            "Information contained on this page is only a guide and doesn't consider your individual objectives, financial situation or needs. Before basing any decisions on this information, please consider:"
          }
        </Text>
        <TextList size="xsmall">
          <Text>its appropriateness to your circumstances</Text>
          <Text>
            obtaining professional advice specific to your needs, including financial, taxation and legal advice.
          </Text>
        </TextList>
        <Stack gap="medium">
          <Text size="xsmall" weight="semibold">
            Brighte
          </Text>
          <Text size="xsmall">
            {`'Brighte' is Brighte Capital Pty Limited (ABN 74 609 165 906). Brighte hosts and manages this platform and provides underlying infrastructure to support the administration of the platform.
        Brighte selects the vendors that may be presented on this platform and facilitates the interactions with vendors on this platform, including listing the products, quoting and installations services provided by the vendors.`}
          </Text>
        </Stack>
        <Stack gap="medium">
          <Text size="xsmall" weight="semibold">
            CommBank
          </Text>
          <Text size="xsmall">
            {`'CommBank' refers to Commonwealth Bank of Australia ABN 48 123 123 124. Australian credit licence 234945. The
        platform is for CommBank customers to help them with their home electrification needs. Home energy upgrade
        suggestions, products, vendor referrals and quotes on this platform and installation services are not conducted
        or provided by CommBank.`}
          </Text>
        </Stack>
        <Text size="xsmall">
          CommBank is not authorised and does not make any assertions or recommendations about reduced consumption from
          non-renewable energy sources through energy-efficient product installations. Advice and further information
          should be sought from relevant certified industry professionals.
        </Text>
        <Text size="xsmall">
          {
            "CommBank receives a fee from Brighte in connection with finance provided by CommBank for a home energy package purchased via Brighte's Marketplace."
          }
        </Text>
        <Text size="xsmall">
          <Trans
            t={t}
            i18nKey="The target market for these CommBank financial products will be found within the product's Target Market Determination, <tmdLink>available here.</tmdLink>"
            components={{
              tmdLink: (
                <TextLink
                  tone="info"
                  target="_blank"
                  href={`${getBrighteSitePartnerUrl(ENTERPRISE_PARTNER_ID.commbank)}/tmd`}
                />
              ),
            }}
          />
        </Text>
        <Text size="xsmall">
          <Trans
            t={t}
            i18nKey="Applications for finance are subject to CommBank's normal credit approval. View the <personalLoanTermsLink>Personal Loan Terms and Conditions</personalLoanTermsLink> and <instalPayTermsLink>InstalPay Terms and Conditions</instalPayTermsLink>. Full terms and conditions will be included in our loan offer. Fees and charges payable."
            components={{
              personalLoanTermsLink: (
                <TextLink
                  tone="info"
                  target="_blank"
                  href={`${getBrighteSitePartnerUrl(ENTERPRISE_PARTNER_ID.commbank)}/personal-loan-terms`}
                />
              ),
              instalPayTermsLink: (
                <TextLink
                  tone="info"
                  target="_blank"
                  href={`${getBrighteSitePartnerUrl(ENTERPRISE_PARTNER_ID.commbank)}/instalpay-terms`}
                />
              ),
            }}
          />
        </Text>
      </Stack>
    ),
  }),
};

export const PARTNER_BRIGHTE_DISCLAIMER: Partial<Record<MarketplacePartner, ReactElement>> = {
  [ENTERPRISE_PARTNER_ID.commbank]: (
    <Text size="xsmall">
      {
        "Brighte is not part of the CBA Group. CommBank and its related entities do not sell, issue or guarantee the obligations or performance of Brighte nor the products and services that Brighte's vendors offer. The relationship with Brighte does not represent a deposit with or liability of either CommBank or any of its related bodies corporate."
      }
    </Text>
  ),
};

// Fallback "what happens next" steps for the success page, used when the
// active channel does not configure its own progressSteps (INT-1119).
export const DEFAULT_SUCCESS_STEPS: { title: string; description: string }[] = [
  {
    title: 'Vendors will be in touch',
    description:
      'Once vendors receive your request, they will contact you in the next couple of days to discuss your request and book a site visit.',
  },
  {
    title: 'Review and accept quotes online',
    description:
      'Brighte will email you when your quote(s) is ready to review. You can select the quote package that best fits your needs and budget.',
  },
  {
    title: 'Installation',
    description:
      'Once your application for finance has been approved, or you have arranged payment with your chosen vendor, they will schedule the installation with you.',
  },
];

export const PARTNER_ACTION_CARD: Partial<
  Record<
    MarketplacePartner,
    {
      icon: string;
      title: ReactElement;
      description: ReactElement;
    }
  >
> = {
  [ENTERPRISE_PARTNER_ID.commbank]: {
    icon: commbankCoinIcon,
    title: (
      <Heading level="3" as="h2">
        Need Financing?
      </Heading>
    ),
    description: (
      <Stack gap="xlarge">
        <Text size="xsmall">
          CommBank has financing options that you can explore to cover the costs of your home energy upgrades. These
          options will be presented to you by CommBank once you accept your quote and select how you would like to pay.
        </Text>
        <Text size="xsmall">
          To find out more about CommBank financing offers, visit commbank.com.au/homeenergyfinance
        </Text>
      </Stack>
    ),
  },
};

export const PARTNER_WITH_PRICING_DISPLAY: MarketplacePartner[] = [
  ENTERPRISE_PARTNER_ID.commbank,
  ENTERPRISE_PARTNER_ID.rewiringAustralia,
  ENTERPRISE_PARTNER_ID.brighte,
];

export const PARTNER_BRIGHTE_MODAL_FOOTER: Partial<Record<MarketplacePartner, string>> = {
  [ENTERPRISE_PARTNER_ID.commbank]: 'Provided by',
};

export const BUNDLE_SUBCATEGORIES: Partial<Record<CATEGORY, CATEGORY[]>> = {
  [CATEGORY.SolarBatteryEvCharger]: [CATEGORY.SolarSystem, CATEGORY.BatteryStorage, CATEGORY.EvCharger],
  [CATEGORY.SolarBatterySystem]: [CATEGORY.SolarSystem, CATEGORY.BatteryStorage],
  [CATEGORY.SolarEvCharger]: [CATEGORY.SolarSystem, CATEGORY.EvCharger],
  [CATEGORY.BatteryEvCharger]: [CATEGORY.BatteryStorage, CATEGORY.EvCharger],
};
